import { CalendarClock, Clock3, Zap } from "lucide-react";
import { dayjs, formatDurationLabel } from "@/utils/date";
import type { CodecEventTimelineEntry } from "../model";

/**
 * Renders one LIC-level device event on the CODEC timeline: the event label,
 * a raw-type tag, the "Em andamento" badge for ongoing events, the
 * human-readable value, and the start/end/duration grid.
 *
 * @param entry - The timeline entry to render.
 */
export function CodecEventTimelineCard({ entry }: { entry: CodecEventTimelineEntry }) {
  const startLabel = dayjs(entry.startTime).format("DD/MM/YYYY HH:mm");
  const endLabel = entry.endTime ? dayjs(entry.endTime).format("DD/MM/YYYY HH:mm") : "Em andamento";

  return (
    <div className="relative pl-10">
      <span className="absolute left-3 top-4 inline-flex h-3 w-3 -translate-x-1/2 rounded-full border border-white bg-blue-500 shadow-sm" />
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{entry.label}</h3>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-500">
                {entry.eventType}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">{entry.valueLabel}</p>
          </div>
          {entry.isOngoing ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
              Em andamento
            </span>
          ) : null}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Início</p>
              <p className="text-sm text-gray-600">{startLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock3 className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Encerramento</p>
              <p className="text-sm text-gray-600">{endLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Duração</p>
              <p className="text-sm text-gray-600">{formatDurationLabel(entry.durationSeconds)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
